<template>
  <div class="relative flex flex-col min-w-0 break-words w-full mb-6 border border-blueGray-200 rounded-lg bg-blueGray-100">

    <!-- Header -->
    <div class="rounded-t bg-white mb-0 px-6 py-4">
      <div class="flex justify-between items-center">
        <h6 class="text-blueGray-700 text-xs font-bold">
          {{ product.id ? 'Edit Product' : 'New Product' }}
        </h6>
        <button
          class="bg-red-500 text-white active:bg-red-600 font-bold uppercase text-xs px-2 py-1 rounded shadow outline-none focus:outline-none ease-linear transition-all duration-150"
          type="button"
          @click="$emit('close')"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Step tabs -->
      <div class="flex mt-4 border-b border-blueGray-200">
        <button
          v-for="(step, i) in steps"
          :key="i"
          class="text-xs px-4 py-2 mr-1 font-semibold border-b-2 transition-colors focus:outline-none"
          :class="activeStep === i
            ? 'border-emerald-500 text-emerald-600'
            : 'border-transparent text-blueGray-400'"
          :disabled="i > 0 && !product.id"
          @click="i === 0 || product.id ? activeStep = i : null"
        >
          <span
            class="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs mr-1 font-bold"
            :class="stepDone(i) ? 'bg-emerald-500 text-white' : 'bg-blueGray-200 text-blueGray-500'"
          >{{ i + 1 }}</span>
          {{ step }}
          <i v-if="i > 0 && !product.id" class="fa fa-lock text-xs ml-1 text-blueGray-300"></i>
        </button>
      </div>
    </div>

    <div class="flex-auto px-4 lg:px-10 py-10 pt-4">

      <!-- ── STEP 1: Basic Details ── -->
      <div v-show="activeStep === 0">
        <h6 class="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase">Product Details</h6>

        <div class="relative w-full mb-3">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">
            Name <span class="text-red-500 normal-case">*</span>
          </label>
          <input
            type="text"
            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
            placeholder="e.g. Lekki Phase 1 Plot"
            v-model="v$.product.name.$model"
            :disabled="isBusy"
          />
          <div class="text-xs text-red-500 mt-1" v-for="(e, i) in v$.product.name.$errors" :key="i">{{ e.$message }}</div>
        </div>

        <div class="relative w-full mb-3">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Sub Title</label>
          <input
            type="text"
            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
            placeholder="e.g. Corner piece, ready to build"
            v-model="product.sub_title"
            :disabled="isBusy"
          />
        </div>

        <div class="flex flex-wrap">
          <div class="w-full lg:w-6/12 pr-2">
            <div class="relative w-full mb-3">
              <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">
                Price (NGN) <span class="text-red-500 normal-case">*</span>
              </label>
              <input
                type="number"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
                placeholder="0.00"
                v-model="v$.product.price.$model"
                :disabled="isBusy"
              />
              <div class="text-xs text-red-500 mt-1" v-for="(e, i) in v$.product.price.$errors" :key="i">{{ e.$message }}</div>
            </div>
          </div>
          <div class="w-full lg:w-6/12 pl-2">
            <div class="relative w-full mb-3">
              <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">
                Available Units <span class="text-red-500 normal-case">*</span>
              </label>
              <input
                type="number"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
                placeholder="0"
                v-model="v$.product.quantity.$model"
                :disabled="isBusy"
              />
              <div class="text-xs text-red-500 mt-1" v-for="(e, i) in v$.product.quantity.$errors" :key="i">{{ e.$message }}</div>
            </div>
          </div>
        </div>

        <div class="relative w-full mb-3">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Discount (NGN)</label>
          <input
            type="number"
            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
            placeholder="0.00"
            v-model="product.discount"
            :disabled="isBusy"
          />
        </div>

        <div class="relative w-full mb-3">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Description</label>
          <textarea
            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
            rows="4"
            placeholder="Describe the property..."
            v-model="product.description"
            :disabled="isBusy"
          ></textarea>
        </div>

        <div class="relative w-full mb-3 mt-4">
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 w-full flex items-center justify-center"
            type="button"
            @click="saveDetails"
            :disabled="isBusy"
          >
            <span v-if="isBusy" class="flex loader loader-sm mr-2"></span>
            {{ product.id ? 'Update Details' : 'Create Product' }}
          </button>
        </div>

        <p v-if="product.id" class="text-xs text-emerald-600 text-center mt-2">
          <i class="fa fa-check-circle mr-1"></i> Product created — continue with the steps above.
        </p>
      </div>

      <!-- ── STEP 2: Category & Plans ── -->
      <div v-show="activeStep === 1">
        <h6 class="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase">Category & Plans</h6>

        <div class="relative w-full mb-6">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Category</label>
          <div class="flex">
            <select
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none flex-grow ease-linear transition-all duration-150 mr-2"
              v-model="product.category_id"
              :disabled="loaders().getCategories || loaders().updateItem"
            >
              <option value="">— none —</option>
              <option v-for="(cat, i) in categories().data" :key="i" :value="cat.id">{{ cat.name }}</option>
            </select>
            <button
              class="bg-blueGray-900 text-white active:bg-blueGray-800 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 flex items-center"
              type="button"
              :disabled="loaders().updateItem"
              @click="saveCategory"
            >
              <span v-if="loaders().updateItem" class="loader loader-sm"></span>
              <span v-else>Save</span>
            </button>
          </div>
        </div>

        <div class="relative w-full mb-3">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Attached Plans</label>

          <p v-if="!(product.plans || []).length" class="text-xs text-blueGray-400 italic mb-3">No plans attached yet.</p>

          <div
            v-for="(plan, i) in (product.plans || [])"
            :key="i"
            class="flex items-center justify-between bg-white rounded shadow px-3 py-2 mb-2"
          >
            <div>
              <p class="text-xs font-semibold text-blueGray-700">{{ plan.name }}</p>
              <p class="text-xs text-blueGray-400">{{ plan.duration }} {{ plan.duration_unit }}(s) · {{ plan.take_profit }}% interest</p>
            </div>
            <button
              class="text-red-500 text-xs font-bold focus:outline-none"
              :disabled="loaders().detachPlan"
              @click="handleDetachPlan(plan.id)"
            >
              <span v-if="loaders().detachPlan" class="loader loader-sm"></span>
              <span v-else><i class="fa fa-unlink mr-1"></i>Remove</span>
            </button>
          </div>

          <hr class="mt-4 mb-4 border-b-1 border-blueGray-300" />
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Attach a Plan</label>
          <div class="flex">
            <select
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none flex-grow ease-linear transition-all duration-150 mr-2"
              v-model="selectedPlanId"
              :disabled="loaders().attachPlan"
            >
              <option value="">— select plan —</option>
              <option v-for="(plan, i) in availablePlansToAttach" :key="i" :value="plan.id">{{ plan.name }}</option>
            </select>
            <button
              class="bg-blueGray-900 text-white active:bg-blueGray-800 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 flex items-center"
              type="button"
              :disabled="!selectedPlanId || loaders().attachPlan"
              @click="handleAttachPlan"
            >
              <span v-if="loaders().attachPlan" class="loader loader-sm"></span>
              <span v-else><i class="fa fa-link mr-1"></i>Attach</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ── STEP 3: Variants ── -->
      <div v-show="activeStep === 2">
        <h6 class="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase">Variants</h6>

        <p v-if="!(product.variants || []).length" class="text-xs text-blueGray-400 italic mb-4">No variants added yet.</p>

        <table v-else class="items-center w-full bg-transparent border-collapse mb-4">
          <thead>
            <tr>
              <th class="px-3 py-2 text-blueGray-500 align-middle border border-solid border-blueGray-100 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50"><i class="pi pi-user mr-1"></i>Name</th>
              <th class="px-3 py-2 text-blueGray-500 align-middle border border-solid border-blueGray-100 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50"><i class="pi pi-tag mr-1"></i>Type</th>
              <th class="px-3 py-2 text-blueGray-500 align-middle border border-solid border-blueGray-100 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50"><i class="pi pi-star mr-1"></i>Point</th>
              <th class="px-3 py-2 text-blueGray-500 align-middle border border-solid border-blueGray-100 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(v, i) in (product.variants || [])" :key="i">
              <td class="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-3 text-blueGray-700 font-semibold">{{ v.variant }}</td>
              <td class="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-3 text-blueGray-500">{{ v.type || '—' }}</td>
              <td class="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-3 text-blueGray-500">{{ v.point != null ? v.point : '—' }}</td>
              <td class="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-3 text-right">
                <button class="text-red-400 focus:outline-none" :disabled="loaders().removeVariant" @click="handleRemoveVariant(v.id)">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="relative flex flex-col min-w-0 break-words bg-white w-full shadow rounded p-4">
          <p class="text-blueGray-600 text-xs font-bold uppercase mb-3">Add Variant</p>
          <div class="flex flex-wrap">
            <div class="w-full lg:w-6/12 pr-2 mb-3">
              <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Name <span class="text-red-500">*</span></label>
              <input type="text" class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-blueGray-50 rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150" placeholder="e.g. Corner Plot" v-model="newVariant.variant" />
            </div>
            <div class="w-full lg:w-6/12 pl-2 mb-3">
              <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Type</label>
              <input type="text" class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-blueGray-50 rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150" placeholder="e.g. size" v-model="newVariant.type" />
            </div>
            <div class="w-full lg:w-6/12 pr-2 mb-3">
              <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Description</label>
              <input type="text" class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-blueGray-50 rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150" placeholder="Optional" v-model="newVariant.description" />
            </div>
            <div class="w-full lg:w-6/12 pl-2 mb-3">
              <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Point</label>
              <input type="number" class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-blueGray-50 rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150" placeholder="0" v-model="newVariant.point" />
            </div>
          </div>
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 w-full flex items-center justify-center"
            type="button"
            :disabled="!newVariant.variant || loaders().addVariant"
            @click="handleAddVariant"
          >
            <span v-if="loaders().addVariant" class="flex loader loader-sm mr-2"></span>
            Add Variant
          </button>
        </div>
      </div>

      <!-- ── STEP 4: Media ── -->
      <div v-show="activeStep === 3">
        <h6 class="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase">Media</h6>

        <div class="relative w-full mb-6">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
            Thumbnails
            <span class="normal-case font-normal text-blueGray-400 ml-1">(max 10MB, any size — Cloudinary resizes automatically)</span>
          </label>
          <input
            type="file"
            accept="image/*"
            class="border-0 px-2 py-1 h-8 bg-white text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
            :disabled="uploading.thumbnail"
            @change="handleUploadThumbnail"
          />
          <p v-if="uploading.thumbnail" class="text-xs text-emerald-600 mt-1"><i>Uploading thumbnail…</i></p>
          <div class="flex flex-wrap mt-3">
            <div v-for="(url, i) in (product.thumbnails || [])" :key="i" class="mr-2 mb-2">
              <img :src="url" class="h-16 w-16 object-cover rounded border border-blueGray-200" />
            </div>
            <p v-if="!(product.thumbnails || []).length" class="text-xs text-blueGray-400 italic">No thumbnails yet.</p>
          </div>
        </div>

        <div class="relative w-full mb-3">
          <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
            Banner
            <span class="normal-case font-normal text-blueGray-400 ml-1">(max 10MB, any size — Cloudinary resizes automatically)</span>
          </label>
          <input
            type="file"
            accept="image/*"
            class="border-0 px-2 py-1 h-8 bg-white text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
            :disabled="uploading.banner"
            @change="handleUploadBanner"
          />
          <p v-if="uploading.banner" class="text-xs text-emerald-600 mt-1"><i>Uploading banner…</i></p>
          <div v-if="product.banner" class="mt-3">
            <img :src="product.banner" class="w-full rounded border border-blueGray-200 object-cover" style="max-height:160px;" />
          </div>
          <p v-else class="text-xs text-blueGray-400 italic mt-2">No banner yet.</p>
        </div>
      </div>

      <!-- ── Publish ── -->
      <div v-if="product.id" class="mt-8 pt-4 border-t border-blueGray-200">
        <button
          class="font-bold uppercase text-sm px-4 py-3 rounded shadow outline-none focus:outline-none ease-linear transition-all duration-150 w-full flex items-center justify-center"
          :class="canPublish
            ? 'bg-blueGray-900 text-white active:bg-blueGray-800 hover:shadow-lg'
            : 'bg-blueGray-200 text-blueGray-400 cursor-not-allowed'"
          type="button"
          :disabled="loaders().publishItem || !canPublish"
          @click="handlePublish"
        >
          <span v-if="loaders().publishItem" class="flex loader loader-sm mr-2"></span>
          <i v-else class="fa fa-rocket mr-2"></i>
          {{ product.status === 'PUBLISHED' ? 'Re-Publish' : 'Publish Product' }}
        </button>
        <p v-if="!canPublish" class="text-xs text-blueGray-400 text-center mt-2">
          Attach at least one plan before publishing.
        </p>
        <p v-if="product.status === 'PUBLISHED'" class="text-xs text-emerald-600 text-center mt-2">
          <i class="fa fa-check-circle mr-1"></i> This product is live.
        </p>
      </div>

    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core';
import { required, decimal } from '@vuelidate/validators';
import { mapActions, mapGetters } from 'vuex';
import { createToaster } from "@meforma/vue-toaster";
import { UploadBanner, UploadThumbnail } from '@/services/item';

const toaster = createToaster({});

export default {
  props: ['selectedProduct'],
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  watch: {
    selectedProduct(p) {
      this.product = p ? { ...p } : {};
      this.activeStep = 0;
    }
  },
  data() {
    return {
      product: this.selectedProduct ? { ...this.selectedProduct } : {},
      activeStep: 0,
      uploading: {},
      selectedPlanId: '',
      newVariant: { variant: '', type: '', description: '', point: null },
      steps: ['Details', 'Category & Plans', 'Variants', 'Media'],
    };
  },
  computed: {
    isBusy() {
      return this.loaders().addItem || this.loaders().updateItem;
    },
    canPublish() {
      return (this.product.plans || []).length > 0;
    },
    availablePlansToAttach() {
      const attached = (this.product.plans || []).map(p => p.id);
      return (this.plans() || []).filter(p => !attached.includes(p.id));
    },
  },
  validations() {
    return {
      product: {
        name:     { required },
        price:    { required, decimal },
        quantity: { required },
      },
    };
  },
  methods: {
    ...mapGetters(['loaders', 'plans', 'categories']),
    ...mapActions([
      'addItem', 'updateItem', 'publishItem',
      'attachPlan', 'detachPlan',
      'getCategories', 'getAllPlans',
      'addVariant', 'removeVariant',
    ]),

    stepDone(i) {
      if (i === 0) return !!this.product.id;
      if (i === 1) return !!this.product.category_id && (this.product.plans || []).length > 0;
      if (i === 2) return (this.product.variants || []).length > 0;
      if (i === 3) return !!(this.product.banner || (this.product.thumbnails || []).length > 0);
      return false;
    },

    async saveDetails() {
      const valid = await this.v$.$validate();
      if (!valid) return;
      const payload = {
        name:        this.product.name,
        sub_title:   this.product.sub_title,
        price:       this.product.price,
        quantity:    this.product.quantity,
        description: this.product.description,
        discount:    this.product.discount || 0,
      };
      try {
        if (this.product.id) {
          const updated = await this.updateItem({ id: this.product.id, ...payload });
          this.product = { ...this.product, ...updated };
        } else {
          const created = await this.addItem(payload);
          this.product = { ...this.product, ...created };
          this.activeStep = 1;
        }
      } catch (e) {
        toaster.error(e?.message || 'Could not save product.');
      }
    },

    async saveCategory() {
      if (!this.product.id) return;
      try {
        const updated = await this.updateItem({ id: this.product.id, category_id: this.product.category_id || null });
        this.product = { ...this.product, ...updated };
      } catch (e) {
        toaster.error(e?.message || 'Could not update category.');
      }
    },

    async handleAttachPlan() {
      if (!this.selectedPlanId || !this.product.id) return;
      try {
        const updated = await this.attachPlan({ itemId: this.product.id, planId: this.selectedPlanId });
        this.product = { ...this.product, ...updated };
        this.selectedPlanId = '';
      } catch (e) {
        toaster.error(e?.message || 'Could not attach plan.');
      }
    },

    async handleDetachPlan(planId) {
      if (!this.product.id) return;
      try {
        const updated = await this.detachPlan({ itemId: this.product.id, planId });
        this.product = { ...this.product, ...updated };
      } catch (e) {
        toaster.error(e?.message || 'Could not remove plan.');
      }
    },

    async handleAddVariant() {
      if (!this.newVariant.variant || !this.product.id) return;
      try {
        const variant = await this.addVariant({ item_id: this.product.id, ...this.newVariant });
        this.product.variants = [...(this.product.variants || []), variant];
        this.newVariant = { variant: '', type: '', description: '', point: null };
      } catch (e) {
        toaster.error(e?.message || 'Could not add variant.');
      }
    },

    async handleRemoveVariant(variantId) {
      if (!this.product.id) return;
      try {
        await this.removeVariant({ itemId: this.product.id, variantId });
        this.product.variants = (this.product.variants || []).filter(v => v.id !== variantId);
      } catch (e) {
        toaster.error(e?.message || 'Could not remove variant.');
      }
    },

    async handleUploadThumbnail(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size / 1024 / 1024 > 10) { toaster.error("Thumbnail must be under 10MB."); return; }
      const formData = new FormData();
      formData.append('file', file);
      this.uploading = { ...this.uploading, thumbnail: true };
      try {
        const res = await UploadThumbnail(formData, this.product.id);
        this.product = {
          ...this.product,
          thumbnails: [...(this.product.thumbnails || []), res.data.data],
        };
      } catch (e) {
        toaster.error(e?.message || 'Thumbnail upload failed.');
      } finally {
        this.uploading = { ...this.uploading, thumbnail: false };
      }
    },

    async handleUploadBanner(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size / 1024 / 1024 > 10) { toaster.error("Banner must be under 10MB."); return; }
      const formData = new FormData();
      formData.append('file', file);
      this.uploading = { ...this.uploading, banner: true };
      try {
        const res = await UploadBanner(formData, this.product.id);
        this.product = { ...this.product, banner: res.data.data };
      } catch (e) {
        toaster.error(e?.message || 'Banner upload failed.');
      } finally {
        this.uploading = { ...this.uploading, banner: false };
      }
    },

    async handlePublish() {
      if (!this.product.id) return;
      try {
        const updated = await this.publishItem({ id: this.product.id });
        this.product = { ...this.product, ...updated };
        toaster.success('Product published successfully!');
      } catch (e) {
        toaster.error(e?.message || 'Could not publish product.');
      }
    },
  },

  mounted() {
    // Products only ever attach to "commerce" plans — restrict the picker to those.
    this.getAllPlans({ q: '?type=commerce' });
    this.getCategories();
  },
};
</script>
